import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { acesso, type Permissao, type UsuarioSessao } from '../api/acesso'
import { ContextoSessao, type EstadoSessao } from './contexto'

/**
 * Recupera a sessão no carregamento perguntando ao servidor quem está logado.
 *
 * O token está em cookie `HttpOnly`, então o JavaScript não consegue lê-lo —
 * de propósito: um script injetado na página leria o `localStorage` inteiro,
 * e não enxerga cookie HttpOnly. O preço é que o front não sabe se há sessão
 * sem perguntar, e esse é o `estado: 'verificando'` do primeiro instante.
 */
export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoSessao>('verificando')
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(null)

  useEffect(() => {
    let vivo = true

    acesso
      .eu()
      .then((u) => {
        if (!vivo) return
        setUsuario(u)
        setEstado('autenticado')
      })
      .catch(() => {
        // 401 aqui é o caso normal de quem ainda não entrou, não um erro a
        // reportar. Qualquer outra falha também termina como anônimo: sem
        // saber quem é, o portal não pode liberar nada.
        if (!vivo) return
        setUsuario(null)
        setEstado('anonimo')
      })

    return () => {
      vivo = false
    }
  }, [])

  const definir = useCallback((novo: UsuarioSessao) => {
    setUsuario(novo)
    setEstado('autenticado')
  }, [])

  const sair = useCallback(async () => {
    try {
      await acesso.sair()
    } finally {
      // Mesmo se a chamada falhar, o front esquece a sessão. O contrário
      // deixaria a tela dizendo "você está dentro" depois de o usuário
      // pedir para sair — e num computador compartilhado isso é grave.
      setUsuario(null)
      setEstado('anonimo')
    }
  }, [])

  const pode = useCallback(
    (permissao: Permissao) => usuario?.permissoes.includes(permissao) ?? false,
    [usuario],
  )

  const valor = useMemo(
    () => ({ estado, usuario, pode, definir, sair }),
    [estado, usuario, pode, definir, sair],
  )

  return <ContextoSessao.Provider value={valor}>{children}</ContextoSessao.Provider>
}
